import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useDeleteVorschau, useTripDelete } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { groesse } from '../../lib/format'
import { useTexte, WORTLAUT } from '../../texte'
import { MedienExport } from '../export/MedienExport'

/** "1 Nachricht" / "3 Nachrichten" -- the plural rule stays in one place. */
function worte(anzahl: number, eins: string, viele: string): string {
  return WORTLAUT.reise.zuruecksetzen.anzahl(anzahl, eins, viele)
}

/**
 * Deleting the trip for good (#15).
 *
 * The step after the reset: the reset keeps the shell, this removes it --
 * cover, background, readers, invitations, everything. Same two steps as
 * there, and for the same reason: first what it costs, counted by the server,
 * then a code to type. A plain "Wirklich?" would be too little for something
 * nobody can bring back.
 *
 * **Before the code comes the reminder** (from the issue: "vor dem Löschen
 * vielleicht ein Hinweis"): the book and the media zip are the two ways to
 * keep the report, and both stand right here so that nobody has to go
 * looking while the deletion is already open.
 *
 * **What confirms the deletion must not live in what it removes** (§6dc):
 * this card is on the trip's own page. So the result is not rendered here --
 * the page navigates to the list, and the list no longer has the trip. That
 * is the confirmation.
 */
/**
 * `privat` says whether the trip stands "nur für dich" -- the same reading of
 * `hidden_at` the info page uses for its state. Only then is the button
 * built; otherwise the card says what to do first and points to the state.
 * The server enforces the same rule (409), this is its visible side.
 */
export function TripDeletion({
  tripId,
  titel,
  privat,
}: {
  tripId: number
  titel: string
  privat: boolean
}) {
  const texte = useTexte()
  const t = texte.reise.loeschen
  const w = texte.reise.zuruecksetzen
  const navigate = useNavigate()
  const [offen, setOffen] = useState(false)
  const [getippt, setGetippt] = useState('')
  const vorschau = useDeleteVorschau(tripId, offen)
  const loeschen = useTripDelete(tripId)
  const online = useOnline()

  const zahlen = vorschau.data
  const passt = zahlen !== undefined && getippt.trim().toUpperCase() === zahlen.code
  // Two answers to "may this trip be deleted": the page's reading of the
  // trip it loaded, and the server's word in the preview. The server's is
  // the fresh one -- changed on another device, the state here is stale
  // until the next load, and the 409 came only after typing the code
  // (review 1.1.0). Both have to say yes.
  const darf = privat && (zahlen === undefined || zahlen.private)

  return (
    <div className="card card--seite" data-reise-loeschen>
      <h2 className="card__titel">{t.titel}</h2>

      <p className="feld__hinweis feld__hinweis--13">{t.was}</p>
      <p className="feld__hinweis feld__hinweis--13">
        {t.sichernVor}
        <strong>{t.sichernFett}</strong>
      </p>

      {/* The two ways to keep the report, before anything is typed. The same
          building blocks as on the "Buch" card, not a second version. */}
      <div className="row kartenloeschen__sichern">
        <Link className="button button--outline button--small" to={`/t/${tripId}/buch`}>
          {texte.reise.info.buch.oeffnen}
        </Link>
        <MedienExport tripId={tripId} />
      </div>

      {!darf && (
        <p className="notice" role="alert" data-hinweis="nicht-privat">
          {t.nurPrivat}{' '}
          <Link to={`/t/${tripId}/info?bereich=reise`}>{t.zumZustand}</Link>
        </p>
      )}

      {darf && !offen && (
        <button
          type="button"
          className="button button--outline button--gefahr"
          onClick={() => {
            setGetippt('')
            setOffen(true)
          }}
        >
          {t.knopf}
        </button>
      )}

      {darf && offen && (
        <div className="stack stack--eintrag">
          {vorschau.isPending && <p className="feld__hinweis">{w.wirdGezaehlt}</p>}

          {vorschau.isError && (
            <p className="notice" role="alert">
              {vorschau.error instanceof Error ? vorschau.error.message : w.uebersichtFehlt}
            </p>
          )}

          {zahlen !== undefined && (
            <>
              <div>
                <p className="kartenloeschen__kopf">{w.verschwindet}</p>
                <ul className="feld__hinweis feld__hinweis--13 kartenloeschen__liste">
                  <li>{t.dieReise(zahlen.title)}</li>
                  <li>{worte(zahlen.messages, w.nachricht, w.nachrichten)}</li>
                  <li>
                    {worte(zahlen.media, w.medium, w.medien)}
                    {zahlen.bytes > 0 && w.mitGroesse(groesse(zahlen.bytes))}
                    {t.mitBildern}
                  </li>
                  <li>{worte(zahlen.tracks, w.gpx, w.gpxMehrere)}</li>
                  <li>{worte(zahlen.highlights, w.punkt, w.punkte)}</li>
                  {zahlen.books > 0 && <li>{worte(zahlen.books, t.buch, t.buecher)}</li>}
                </ul>
              </div>

              {zahlen.readers > 0 && (
                <p className="notice" role="alert">
                  {worte(zahlen.readers, w.leserEins, w.leserViele)}
                  {t.leserWarnung}
                </p>
              )}

              <label className="field">
                <span className="field__label">
                  {w.abtippenVor}
                  <strong>{zahlen.code}</strong>
                </span>
                <input
                  className="field__input"
                  value={getippt}
                  onChange={(event) => setGetippt(event.target.value)}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label={w.abtippenLabel(zahlen.code)}
                  placeholder={zahlen.code}
                />
              </label>

              {loeschen.isError && (
                <p className="notice" role="alert">
                  {loeschen.error instanceof Error
                    ? loeschen.error.message
                    : texte.allgemein.schiefgelaufen}
                </p>
              )}

              {!online && (
                <p className="feld__hinweis" role="alert">
                  {w.ohneNetz}
                </p>
              )}

              <div className="row kartenloeschen__knoepfe">
                <button
                  type="button"
                  className="button button--outline button--small"
                  onClick={() => setOffen(false)}
                >
                  {texte.allgemein.abbrechen}
                </button>
                <button
                  type="button"
                  className="button button--outline button--small button--gefahr"
                  disabled={!passt || !online || loeschen.isPending}
                  onClick={async () => {
                    await loeschen.mutateAsync(getippt)
                    // The page this card lives on is gone with the trip.
                    navigate('/', { replace: true })
                  }}
                >
                  {loeschen.isPending ? t.laeuft : t.endgueltig(titel)}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
